import { ORDER_TICKET_FIXED_WIDTH_PX } from "./placementAdapters";
import type { WidgetPlacement, WorkspaceSnapshot } from "./workspace.types";
import {
  WORKSPACE_TEMPLATES,
  type WorkspaceTemplateId,
} from "./workspaceTemplates";

export { ORDER_TICKET_FIXED_WIDTH_PX };

const overlapsVertically = (a: WidgetPlacement, b: WidgetPlacement): boolean =>
  a.y < b.y + b.h && b.y < a.y + a.h;

const isManagedTicketPin = (placement: WidgetPlacement): boolean =>
  placement.alignX === "end" &&
  placement.fixedWidthPx === ORDER_TICKET_FIXED_WIDTH_PX;

/** Drops template pin flags so leftover tickets cannot share the right-edge slot. */
const withoutManagedTicketPin = (
  placement: WidgetPlacement,
): WidgetPlacement => {
  if (!isManagedTicketPin(placement)) {
    return placement;
  }
  const { alignX: _alignX, fixedWidthPx: _fixedWidthPx, ...rest } = placement;
  return rest;
};

/**
 * Prefers the saved placement that already carries the template pin metadata,
 * then the closest geometry. `toSnapshot` sorts widgets by id, so first-unused
 * same-kind matching can bind a user-added ticket after instantiate remaps ids.
 */
const sameKindPlacementScore = (
  templatePlacement: WidgetPlacement | undefined,
  savedPlacement: WidgetPlacement | undefined,
): number => {
  if (templatePlacement === undefined || savedPlacement === undefined) {
    return Number.NEGATIVE_INFINITY;
  }

  let score = 0;
  if (
    templatePlacement.alignX !== undefined &&
    savedPlacement.alignX === templatePlacement.alignX
  ) {
    score += 1_000_000;
  }
  if (
    templatePlacement.fixedWidthPx !== undefined &&
    savedPlacement.fixedWidthPx === templatePlacement.fixedWidthPx
  ) {
    score += 1_000_000;
  }
  if (
    templatePlacement.minWidthPx !== undefined &&
    savedPlacement.minWidthPx === templatePlacement.minWidthPx
  ) {
    score += 100_000;
  }

  const dx = savedPlacement.x - templatePlacement.x;
  const dy = savedPlacement.y - templatePlacement.y;
  const dw = savedPlacement.w - templatePlacement.w;
  const dh = savedPlacement.h - templatePlacement.h;
  return score - (dx * dx + dy * dy + dw * dw + dh * dh);
};

const pickBestSameKindCandidate = (
  candidates: WorkspaceSnapshot["widgets"],
  templatePlacement: WidgetPlacement | undefined,
  savedPlacementById: Map<string, WidgetPlacement>,
): WorkspaceSnapshot["widgets"][number] =>
  candidates.reduce((best, candidate) => {
    const candidateScore = sameKindPlacementScore(
      templatePlacement,
      savedPlacementById.get(candidate.id),
    );
    const bestScore = sameKindPlacementScore(
      templatePlacement,
      savedPlacementById.get(best.id),
    );
    return candidateScore > bestScore ? candidate : best;
  });

/** Maps template geometry onto the saved widgets (same id, then best same kind). */
const remapPlacementsFromTemplate = (
  workspace: WorkspaceSnapshot,
  template: WorkspaceSnapshot,
): WidgetPlacement[] => {
  const savedById = new Map(
    workspace.widgets.map((widget) => [widget.id, widget]),
  );
  const savedPlacementById = new Map(
    workspace.placements.map((placement) => [placement.widgetId, placement]),
  );
  const templatePlacementById = new Map(
    template.placements.map((placement) => [placement.widgetId, placement]),
  );
  const usedSavedIds = new Set<string>();
  const templateIdToSavedId = new Map<string, string>();

  for (const templateWidget of template.widgets) {
    const saved = savedById.get(templateWidget.id);
    if (saved?.kind === templateWidget.kind) {
      templateIdToSavedId.set(templateWidget.id, saved.id);
      usedSavedIds.add(saved.id);
    }
  }

  for (const templateWidget of template.widgets) {
    if (templateIdToSavedId.has(templateWidget.id)) {
      continue;
    }
    const candidates = workspace.widgets.filter(
      (widget) =>
        widget.kind === templateWidget.kind && !usedSavedIds.has(widget.id),
    );
    if (candidates.length === 0) {
      continue;
    }
    const saved = pickBestSameKindCandidate(
      candidates,
      templatePlacementById.get(templateWidget.id),
      savedPlacementById,
    );
    templateIdToSavedId.set(templateWidget.id, saved.id);
    usedSavedIds.add(saved.id);
  }

  const remapped = template.placements.flatMap((placement) => {
    const widgetId = templateIdToSavedId.get(placement.widgetId);
    return widgetId === undefined ? [] : [{ ...placement, widgetId }];
  });
  const remappedSavedIds = new Set(
    remapped.map((placement) => placement.widgetId),
  );
  const extras = workspace.placements
    .filter((placement) => !remappedSavedIds.has(placement.widgetId))
    .map(withoutManagedTicketPin);
  return [...remapped, ...extras];
};

/** Moves a ticket past any same-row neighbor so it can occupy the right edge. */
const giveOrderTicketTheRightmostSlot = (
  placements: WidgetPlacement[],
  ticketId: string,
): WidgetPlacement[] => {
  const ticket = placements.find(
    (placement) => placement.widgetId === ticketId,
  );
  if (!ticket) {
    return placements;
  }

  const rightmostNeighbor = placements
    .filter(
      (placement) =>
        placement.widgetId !== ticketId &&
        overlapsVertically(placement, ticket),
    )
    .sort((a, b) => b.x + b.w - (a.x + a.w))[0];

  if (
    !rightmostNeighbor ||
    rightmostNeighbor.x + rightmostNeighbor.w <= ticket.x + ticket.w
  ) {
    return placements;
  }

  return placements.map((placement) => {
    if (placement.widgetId === ticket.widgetId) {
      return { ...placement, x: rightmostNeighbor.x };
    }
    if (placement.widgetId === rightmostNeighbor.widgetId) {
      return { ...placement, x: ticket.x };
    }
    return placement;
  });
};

/** Templates that author row gaps and must not re-compact on hydrate. */
const isDeskEvaluationTemplateId = (id: WorkspaceTemplateId): boolean =>
  WORKSPACE_TEMPLATES[id].gridSettings.compactor === "none";

export const withManagedTemplateGeometry = (
  workspace: WorkspaceSnapshot,
  templateId?: WorkspaceTemplateId,
): WorkspaceSnapshot => {
  if (!templateId) {
    return workspace;
  }

  let placements = remapPlacementsFromTemplate(
    workspace,
    WORKSPACE_TEMPLATES[templateId].workspace,
  );

  // Remap copies pin flags only onto the template-matched ticket. Extras are
  // stripped above so they cannot share the right-edge slot.
  const managedTicketIds = [
    ...new Set(
      placements
        .filter(isManagedTicketPin)
        .map((placement) => placement.widgetId),
    ),
  ];

  for (const ticketId of managedTicketIds) {
    placements = giveOrderTicketTheRightmostSlot(placements, ticketId);
  }

  return { ...workspace, placements };
};

/** Stops vertical compact from collapsing Equities/Options desk rows. */
export const withDeskEvaluationGridSettings = (
  settings: Record<string, unknown>,
  templateId?: WorkspaceTemplateId,
): Record<string, unknown> => {
  if (!templateId || !isDeskEvaluationTemplateId(templateId)) {
    return settings;
  }
  if (settings.compactor === "none") {
    return settings;
  }
  return { ...settings, compactor: "none" };
};
